import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = '/api/v1';

export interface CallLogEntry {
  callId: string;
  agentId: string;
  agentName: string;
  virtualInterfaceId: string;
  channelType: string;
  status: string;
  duration: number;
  startedAt: string | null;
  endedAt: string | null;
}

export interface UseRecentCallsReturn {
  calls: CallLogEntry[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRecentCalls(limit = 10): UseRecentCallsReturn {
  const { isAuthenticated } = useAuth();
  const [calls, setCalls] = useState<CallLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchCount, setRefetchCount] = useState(0);

  const refetch = useCallback(() => setRefetchCount((n) => n + 1), []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchCalls = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_BASE_URL}/dashboard/call-log`, {
          params: { limit },
        });
        const items = response.data?.data?.items ?? response.data?.items ?? [];
        setCalls(items);
      } catch (err: any) {
        setError(err.response?.data?.message ?? 'Failed to load recent calls');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCalls();
  }, [isAuthenticated, limit, refetchCount]);

  return { calls, isLoading, error, refetch };
}
